// Custom confirmation dialog and window.confirm override
// This module defines a Promise-based customConfirm() and overrides window.confirm
// to display a Bootstrap-based dialog for user-triggered interactions.

(function initCustomConfirmModule() {
  if (window.__customConfirmInitialized) return;
  window.__customConfirmInitialized = true;

  const originalConfirm = window.confirm.bind(window);
  let customConfirmId = 0;

  /**
   * Display a Bootstrap-based confirmation modal.
   * @param {string|object} messageOrOptions - Message string or options object
   * @param {function} [onConfirm] - Callback on confirmation
   * @param {function} [onCancel] - Callback on cancellation
   * @returns {Promise<boolean>}
   */
  function customConfirm(messageOrOptions, onConfirm, onCancel) {
    const defaults = { title: null, message: '', confirmText: 'Yes', cancelText: 'No' };
    const opts = typeof messageOrOptions === 'object' && messageOrOptions !== null
      ? { ...defaults, ...messageOrOptions }
      : { ...defaults, message: String(messageOrOptions || '') };

    return new Promise((resolve) => {
      // Use jQuery/Bootstrap if available
      if (typeof $ === 'undefined' || typeof $.fn.modal === 'undefined') {
        const result = originalConfirm(opts.message);
        if (result) onConfirm?.(); else onCancel?.();
        return resolve(result);
      }

      const modalHtml = `
        <div class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
          <div class="modal-dialog modal-sm" role="document">
            <div class="modal-content">
              ${opts.title ? `
                <div class="modal-header">
                  <button type="button" class="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                  <h4 class="modal-title">${opts.title}</h4>
                </div>
              ` : ''}
              <div class="modal-body text-center">
                <p>${opts.message}</p>
              </div>
              <div class="modal-footer" style="text-align: center">
                <button type="button" class="btn btn-danger" data-dismiss="modal">${opts.cancelText}</button>
                <button type="button" class="btn btn-success btn-confirm">${opts.confirmText}</button>
              </div>
            </div>
          </div>
        </div>
      `;

      const $modal = $(modalHtml).appendTo('body');
      let resolved = false;
      const keydownEvent = `keydown.customConfirm${customConfirmId += 1}`;

      const removeKeyboardHandler = () => {
        $(document).off(keydownEvent);
      };

      const finish = (result) => {
        if (resolved) return;
        resolved = true;

        removeKeyboardHandler();
        $modal.modal('hide');
        if (result) onConfirm?.(); else onCancel?.();
        resolve(result);
      };

      $modal.modal('show');
      $modal.find('.btn-confirm').on('click', () => finish(true));
      $(document).on(keydownEvent, (e) => {
        if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
          e.preventDefault();
          e.stopPropagation();
          finish(false);
        } else if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          e.stopPropagation();
          finish(true);
        }
      });
      $modal.on('hidden.bs.modal', () => {
        finish(false);
        removeKeyboardHandler();
        $modal.remove();
      });
    });
  }

  window.customConfirm = customConfirm;

  // Track last user event target to enable re-dispatching after async confirmation
  let lastEventTarget = null;
  const captureTarget = (e) => { lastEventTarget = e.target; };
  document.addEventListener('click', captureTarget, true);
  document.addEventListener('submit', captureTarget, true);

  function closestActionElement(el) {
    return (el && typeof el.closest === 'function') ? el.closest('a, button, input, form') || el : el;
  }

  function temporarilyBypassConfirm(element) {
    if (!element || !element.getAttribute) return () => {};
    const originalAttr = element.getAttribute('data-confirm');
    element.removeAttribute('data-confirm');
    element.__customConfirmBypass = true;
    return () => {
      if (originalAttr) element.setAttribute('data-confirm', originalAttr);
      delete element.__customConfirmBypass;
    };
  }

  window.confirm = function(message) {
    try {
      // Use native confirm if no user event context (sync semantics) or already bypassed
      if (!lastEventTarget) return originalConfirm(message);

      const actionEl = closestActionElement(lastEventTarget);
      if (actionEl?.__customConfirmBypass) return true;

      const opts = { message: message };
      if (actionEl) {
        const title = actionEl.getAttribute('data-confirm-title') || actionEl.getAttribute('data-confirm_title');
        const yes = actionEl.getAttribute('data-confirm-yes') || actionEl.getAttribute('data-confirm_yes');
        const no = actionEl.getAttribute('data-confirm-no') || actionEl.getAttribute('data-confirm_no');

        if (title) opts.title = title;
        if (yes) opts.confirmText = yes;
        if (no) opts.cancelText = no;
      }

      customConfirm(opts).then((confirmed) => {
        if (!confirmed || !actionEl) return;

        const restore = temporarilyBypassConfirm(actionEl);

        if (actionEl.tagName === 'FORM') {
          if (window.Rails && typeof Rails.fire === 'function') {
            Rails.fire(actionEl, 'submit');
          } else {
            actionEl.submit?.();
          }
        } else {
          if (typeof actionEl.click === 'function') {
            actionEl.click();
          } else {
            actionEl.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
          }
        }

        setTimeout(restore, 0);
      });

      return false;
    } catch (e) {
      return originalConfirm(message);
    } finally {
      // Clear lastEventTarget after the current call stack
      setTimeout(() => { lastEventTarget = null; }, 0);
    }
  };
})();

export {};
